import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  CreditCard,
  Shield,
  Zap,
  Crown,
  Loader2,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function Billing({ user, logout, token }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Handle PayPal return
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    if (paymentId && payerId) {
      handlePaymentReturn(paymentId, payerId);
    }
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/plans`),
        axios.get(`${BACKEND_URL}/api/subscription`, { headers, withCredentials: true })
      ]);
      setPlans(plansRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentReturn = async (paymentId, payerId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/execute`,
        null,
        { 
          headers, 
          withCredentials: true,
          params: { payment_id: paymentId, payer_id: payerId }
        }
      );
      
      if (response.data.success) {
        toast.success(`Welcome to the ${response.data.plan} plan!`);
        fetchData();
        // Clear URL params
        navigate('/billing', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment processing failed");
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/create`,
        {
          plan: planId,
          return_url: `${window.location.origin}/billing?success=true`,
          cancel_url: `${window.location.origin}/billing?canceled=true`
        },
        { headers, withCredentials: true }
      );
      
      if (response.data.success && response.data.approval_url) {
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      } else {
        toast.error("Failed to create payment. Please configure PayPal credentials.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return Shield;
      case 'professional': return Zap;
      case 'enterprise': return Crown;
      default: return Shield;
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading billing...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="billing-page">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold font-[Chivo]">Billing & Subscription</h2>
          <p className="text-muted-foreground">
            Manage your subscription and payment methods
          </p>
        </div>

        {/* Current Subscription */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-[Chivo]">Current Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-sm flex items-center justify-center ${
                  subscription?.plan === 'professional' ? 'bg-primary/20' :
                  subscription?.plan === 'enterprise' ? 'bg-yellow-500/20' :
                  'bg-muted'
                }`}>
                  {subscription?.plan === 'professional' ? (
                    <Zap className="h-6 w-6 text-primary" />
                  ) : subscription?.plan === 'enterprise' ? (
                    <Crown className="h-6 w-6 text-yellow-500" />
                  ) : (
                    <Shield className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-bold capitalize">{subscription?.plan || 'Free'} Plan</p>
                  <p className="text-sm text-muted-foreground">
                    {subscription?.status === 'active' ? (
                      <span className="text-emerald-500">Active</span>
                    ) : subscription?.status === 'expired' ? (
                      <span className="text-red-500">Expired</span>
                    ) : (
                      <span>Free tier</span>
                    )}
                    {subscription?.expires_at && (
                      <span> • Renews {new Date(subscription.expires_at).toLocaleDateString()}</span>
                    )}
                  </p>
                </div>
              </div>
              {subscription?.scan_limit && (
                <Badge variant="outline">
                  {subscription.scan_limit === -1 ? 'Unlimited' : subscription.scan_limit} scans/month
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* PayPal Notice */}
        {!PAYPAL_CLIENT_ID && (
          <Card className="bg-yellow-500/5 border-yellow-500/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-500">PayPal Not Configured</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    To enable payments, add your PayPal credentials to the backend .env file:
                    PAYPAL_CLIENT_ID and PAYPAL_CLIENT_SECRET
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(plans).map(([planId, plan]) => {
            const Icon = getPlanIcon(planId);
            const isCurrentPlan = subscription?.plan === planId;
            const isPopular = planId === 'professional';
            
            return (
              <motion.div
                key={planId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={`relative bg-card h-full flex flex-col ${
                  isPopular ? 'border-primary glow-primary' : 'border-border'
                }`}>
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${
                        isPopular ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        <Icon className={`h-5 w-5 ${isPopular ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <CardTitle className="font-[Chivo]">{plan.name}</CardTitle>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-black font-[Chivo] text-primary">
                        {plan.price === 0 ? 'Custom' : `$${plan.price}`}
                      </span>
                      {plan.price > 0 && <span className="text-muted-foreground">/month</span>}
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <ul className="space-y-3 flex-1">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      {isCurrentPlan ? (
                        <Button className="w-full" disabled variant="outline">
                          Current Plan
                        </Button>
                      ) : plan.price === 0 ? (
                        <Button className="w-full" variant="outline">
                          Contact Sales
                        </Button>
                      ) : (
                        <Button 
                          className={`w-full ${isPopular ? 'glow-primary' : ''}`}
                          variant={isPopular ? 'default' : 'outline'}
                          onClick={() => handleSubscribe(planId)}
                          disabled={processingPlan === planId}
                          data-testid={`subscribe-${planId}-btn`}
                        >
                          {processingPlan === planId ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-4 w-4 mr-2" />
                              Subscribe with PayPal
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Payment Security */}
        <Card className="bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Shield className="h-8 w-8 text-primary" />
              <div>
                <p className="font-medium">Secure Payments</p>
                <p className="text-sm text-muted-foreground">
                  All payments are processed securely through PayPal. We never store your payment information.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
